import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSignal, createSignal, updateSignal } from '../services/api';
import Modal from '../components/Modal';
import {
  Box, Typography, Button, Paper, Stack, Chip, TextField, MenuItem,
  Alert, CircularProgress, Grid, FormControlLabel, Checkbox, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SIGNAL_CATEGORIES = ['PainPoint', 'Industry', 'Technology', 'CompanyType', 'Feature', 'UseCase', 'Hiring', 'Competitive'];

function safeParse(val, fallback) {
  if (val === null || val === undefined) return fallback;
  if (typeof val !== 'string') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

// ── Reusable list section (same pattern as MarketingBrainPage) ──

function ArrayListSection({ title, icon, description, items, onAdd, onAddMultiple, onEdit, onDelete }) {
  return (
    <Paper sx={{ p: 3, mb: 2.5 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{icon}</span> {title}
            <Chip size="small" label={items.length} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={onAddMultiple}>+ Add Multiple</Button>
          <Button variant="contained" size="small" onClick={onAdd}>+ Add</Button>
        </Stack>
      </Stack>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 2.5 }}>
          No items added yet
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {items.map((item, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 0 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
                <Typography variant="body2" sx={{ flex: 1, mr: 1.5 }}>{item}</Typography>
                <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                  <Button variant="outlined" size="small" onClick={() => onEdit(idx)}>Edit</Button>
                  <Button variant="outlined" color="error" size="small" onClick={() => onDelete(idx)}>Delete</Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

// ── Main Page ──

export default function SignalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Form state ──
  const [text, setText] = useState('');
  const [category, setCategory] = useState('PainPoint');
  const [weight, setWeight] = useState(50);
  const [isActive, setIsActive] = useState(true);
  const [description, setDescription] = useState('');
  const [examples, setExamples] = useState([]);

  // ── Modal state ──
  const [itemModal, setItemModal] = useState(null); // { type: 'single'|'multi', editIdx? }
  const [modalValue, setModalValue] = useState('');

  // ── Load existing signal ──
  const loadSignal = useCallback(async () => {
    try {
      const { data } = await getSignal(id);
      setText(data.text || '');
      setCategory(data.category || 'PainPoint');
      setWeight(data.weight || 50);
      setIsActive(data.isActive !== false);
      setDescription(data.description || '');
      setExamples(safeParse(data.examples, []));
    } catch {
      setError('Failed to load signal');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) loadSignal();
  }, [isNew, loadSignal]);

  // ── Example handlers ──
  const openAddItem = () => {
    setModalValue('');
    setItemModal({ type: 'single', editIdx: null });
  };

  const openAddMultiple = () => {
    setModalValue('');
    setItemModal({ type: 'multi' });
  };

  const openEditItem = (idx) => {
    setModalValue(examples[idx] || '');
    setItemModal({ type: 'single', editIdx: idx });
  };

  const handleDeleteItem = (idx) => {
    setExamples(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveItem = () => {
    if (!modalValue.trim()) return;
    const { editIdx } = itemModal;
    setExamples(prev => {
      const arr = [...prev];
      if (editIdx !== null) {
        arr[editIdx] = modalValue.trim();
      } else {
        arr.push(modalValue.trim());
      }
      return arr;
    });
    setItemModal(null);
  };

  const handleSaveMultiple = () => {
    if (!modalValue.trim()) return;
    const lines = modalValue.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    setExamples(prev => [...prev, ...lines]);
    setItemModal(null);
  };

  // ── Save signal ──
  const handleSave = async () => {
    if (!text.trim()) { setError('Signal text is required'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        text: text.trim(),
        category,
        weight: parseInt(weight, 10) || 50,
        isActive,
        description: description.trim(),
        examples: JSON.stringify(examples),
      };
      if (isNew) {
        await createSignal(payload);
      } else {
        await updateSignal(id, payload);
      }
      navigate('/management?tab=signals');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save signal');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Back link + header */}
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/management?tab=signals')} sx={{ mb: 2 }}>
        Back to Management
      </Button>

      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{isNew ? 'New Signal' : 'Edit Signal'}</Typography>
          <Typography variant="body2" color="text.secondary">Configure a signal — observable indicators that help AI identify and score leads.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/management?tab=signals')}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create Signal' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Section 1: Basic Information ── */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📡</span> Basic Information
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Core properties of this signal.</Typography>
        <TextField
          label="Signal Text *"
          size="small"
          fullWidth
          required
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. subscription billing pain, scaling challenges"
          sx={{ mb: 2 }}
        />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Category *"
              size="small"
              fullWidth
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {SIGNAL_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Weight (1–100) *"
              type="number"
              size="small"
              fullWidth
              slotProps={{ htmlInput: { min: 1, max: 100 } }}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            />
          </Grid>
        </Grid>
        <TextField
          label="Description"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this signal indicates about a lead"
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
          label="Active"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          Inactive signals are excluded from AI searches and scoring.
        </Typography>
      </Paper>

      {/* ── Section 2: Examples ── */}
      <ArrayListSection
        title="Examples"
        icon="💬"
        description="Example phrases or evidence that would trigger this signal. Helps AI match leads more accurately."
        items={examples}
        onAdd={openAddItem}
        onAddMultiple={openAddMultiple}
        onEdit={openEditItem}
        onDelete={handleDeleteItem}
      />

      {/* ── Bottom save bar ── */}
      <Paper elevation={3} sx={{ position: 'sticky', bottom: 0, p: 2, mt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => navigate('/management?tab=signals')}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create Signal' : 'Save Changes'}
        </Button>
      </Paper>

      {/* ── Single Item Modal ── */}
      {itemModal && itemModal.type === 'single' && (
        <Modal
          title={itemModal.editIdx !== null ? 'Edit Example' : 'Add Example'}
          onClose={() => setItemModal(null)}
        >
          <TextField
            label="Example"
            size="small"
            fullWidth
            multiline
            rows={4}
            value={modalValue}
            onChange={(e) => setModalValue(e.target.value)}
            placeholder="Enter example phrase…"
            autoFocus
          />
          <DialogActions sx={{ px: 0, pb: 0, mt: 2 }}>
            <Button variant="outlined" onClick={() => setItemModal(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveItem} disabled={!modalValue.trim()}>
              {itemModal.editIdx !== null ? 'Save' : 'Add'}
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* ── Add Multiple Modal ── */}
      {itemModal && itemModal.type === 'multi' && (
        <Modal
          title="Add Multiple Examples"
          onClose={() => setItemModal(null)}
        >
          <TextField
            label="Examples (one per line)"
            size="small"
            fullWidth
            multiline
            rows={10}
            value={modalValue}
            onChange={(e) => setModalValue(e.target.value)}
            placeholder={"Enter one example per line, e.g.:\nFirst example phrase\nSecond example phrase\nThird example phrase"}
            autoFocus
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {modalValue.trim() ? `${modalValue.split('\n').map(l => l.trim()).filter(Boolean).length} item(s) will be added` : 'Paste or type multiple examples, each on its own line'}
          </Typography>
          <DialogActions sx={{ px: 0, pb: 0, mt: 2 }}>
            <Button variant="outlined" onClick={() => setItemModal(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveMultiple} disabled={!modalValue.trim()}>
              Add All
            </Button>
          </DialogActions>
        </Modal>
      )}
    </Box>
  );
}
